import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { emptyRange, nextVerseRange } from "../../bible/verseSelection";
import { buildSelectionFormatText, cleanVerseQuoteText } from "../../bible/verseQuote";
import { buildShareUrl, parseVerseParam } from "../../bible/shareUrl";
import { revealElementAboveSelectionDock } from "../../theme/readerLayout";

const getSelectedVersesFromSelection = (selection, container) => {
  if (!selection || !container || selection.rangeCount === 0) return [];
  const range = selection.getRangeAt(0);
  const verseNodes = container.querySelectorAll("[data-verse]");
  const verses = [];
  verseNodes.forEach((node) => {
    try {
      if (range.intersectsNode(node)) {
        const value = Number(node.getAttribute("data-verse"));
        if (Number.isFinite(value)) {
          verses.push(value);
        }
      }
    } catch (error) {
      const verseAttr = node.getAttribute("data-verse");
      if (verseAttr) {
        const value = Number(verseAttr);
        if (Number.isFinite(value)) {
          const nodeRange = document.createRange();
          nodeRange.selectNodeContents(node);
          if (
            range.compareBoundaryPoints(Range.START_TO_END, nodeRange) >= 0 &&
            range.compareBoundaryPoints(Range.END_TO_START, nodeRange) <= 0
          ) {
            verses.push(value);
          }
        }
      }
    }
  });
  return verses;
};

export const copyToClipboard = async (value) => {
  if (!value) return;
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(value);
    return;
  }
  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

export const useChapterSelection = ({
  chapterId,
  chapter,
  bookName,
  loading,
  focusVerse,
  edition,
  verseTextMap,
  neighborVerse,
  versesRef,
  isMobile,
  noteEditorOpen,
}) => {
  const [selectedRange, setSelectedRange] = useState(emptyRange);
  const [selectionAnchor, setSelectionAnchor] = useState(null);
  const selectedRangeRef = useRef(selectedRange);

  useEffect(() => {
    selectedRangeRef.current = selectedRange;
  }, [selectedRange]);

  useEffect(() => {
    setSelectedRange(emptyRange);
    setSelectionAnchor(null);
  }, [chapterId, edition.id]);

  useEffect(() => {
    if (!focusVerse || loading || !chapter) return undefined;
    const parsed = parseVerseParam(focusVerse);
    if (!parsed) return undefined;

    setSelectedRange({ start: parsed.start, end: parsed.end });
    setSelectionAnchor(parsed.start);

    const timer = window.setTimeout(() => {
      const node = versesRef.current?.querySelector(
        `[data-verse="${parsed.start}"]`
      );
      if (node) {
        node.scrollIntoView({ block: "center", behavior: "smooth" });
      }
    }, 80);
    return () => window.clearTimeout(timer);
  }, [chapter, focusVerse, loading, versesRef]);

  const buildChapterReference = useCallback(
    (startVerse, endVerse) => {
      if (!bookName || !chapter) return "";
      const chapterPart = `${bookName} ${chapter.chapterNumber}`;
      if (!startVerse) return chapterPart;
      if (!endVerse || startVerse === endVerse) {
        return `${chapterPart}:${startVerse}`;
      }
      return `${chapterPart}:${startVerse}-${endVerse}`;
    },
    [bookName, chapter]
  );

  const collectVerses = useCallback(
    (start, end, mapper) => {
      if (!start || !end) return [];
      const collected = [];
      for (let verse = start; verse <= end; verse += 1) {
        const verseText = verseTextMap[String(verse)];
        if (verseText) {
          collected.push(mapper(verse, verseText));
        }
      }
      return collected;
    },
    [verseTextMap]
  );

  const buildFormatText = useCallback(
    (format, start, end) => {
      if (!start || !end) return "";
      return buildSelectionFormatText({
        format,
        verses: collectVerses(start, end, (number, text) => ({ number, text })),
        reference: buildChapterReference(start, end),
        shareUrl: buildShareUrl(edition.id, chapterId, start, end),
        quoteDash: edition.quoteDash,
      });
    },
    [buildChapterReference, chapterId, collectVerses, edition.id, edition.quoteDash]
  );

  const buildEnhancedText = useCallback(
    (number, text) =>
      buildSelectionFormatText({
        format: "reference",
        verses: [{ number, text }],
        reference: buildChapterReference(number, number),
        shareUrl: buildShareUrl(edition.id, chapterId, number, number),
      }),
    [buildChapterReference, chapterId, edition.id]
  );

  const handleCopyTextOnly = useCallback(
    (text) => copyToClipboard(cleanVerseQuoteText(text)),
    []
  );

  const handleCopyEnhanced = useCallback(
    (number, text) => copyToClipboard(buildEnhancedText(number, text)),
    [buildEnhancedText]
  );

  const handleShareWhatsApp = useCallback(
    (number, text) => {
      const payload = buildEnhancedText(number, text);
      const url = `https://wa.me/?text=${encodeURIComponent(payload)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [buildEnhancedText]
  );

  const applyRange = useCallback((start, end, anchor) => {
    setSelectedRange({ start, end });
    if (anchor !== undefined) {
      setSelectionAnchor(anchor);
    }
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedRange(emptyRange);
    setSelectionAnchor(null);
    const selection = window.getSelection();
    if (selection && selection.rangeCount) {
      selection.removeAllRanges();
    }
  }, []);

  const handleVerseTap = useCallback(
    (rawNumber, { shiftKey } = {}) => {
      const verse = Number(rawNumber);
      if (!Number.isFinite(verse)) return;

      const current = selectedRangeRef.current;
      if (shiftKey && current.start && selectionAnchor) {
        applyRange(
          Math.min(selectionAnchor, verse),
          Math.max(selectionAnchor, verse)
        );
        return;
      }

      const next = nextVerseRange(current, verse, neighborVerse);
      if (!next.start) {
        clearSelection();
        return;
      }
      applyRange(next.start, next.end, next.anchor);
    },
    [applyRange, clearSelection, neighborVerse, selectionAnchor]
  );

  const commitNativeSelection = useCallback(() => {
    if (isMobile) return;
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;
    const container = versesRef.current;
    if (!container) return;
    const { anchorNode, focusNode } = selection;
    const inContainer =
      anchorNode &&
      focusNode &&
      container.contains(anchorNode) &&
      container.contains(focusNode);
    if (!inContainer) return;
    const selectedVerses = getSelectedVersesFromSelection(selection, container);
    if (!selectedVerses.length) return;
    const start = Math.min(...selectedVerses);
    const end = Math.max(...selectedVerses);
    applyRange(start, end, start);
    selection.removeAllRanges();
  }, [applyRange, isMobile, versesRef]);

  const isVerseSelected = useCallback(
    (number) => {
      if (!number || !selectedRange.start) return false;
      const verseNumber = Number(number);
      return (
        Number.isFinite(verseNumber) &&
        verseNumber >= selectedRange.start &&
        verseNumber <= selectedRange.end
      );
    },
    [selectedRange.end, selectedRange.start]
  );

  const handleVerseClick = useCallback(
    (event, number) => {
      if (event.target.closest(".verse-actions-inline")) return;
      const selection = window.getSelection();
      if (selection && !selection.isCollapsed && selection.toString().trim()) {
        return;
      }
      handleVerseTap(number, { shiftKey: event.shiftKey });
    },
    [handleVerseTap]
  );

  const expandStart = useCallback(() => {
    if (!selectedRange.start) return;
    const prev = neighborVerse(selectedRange.start, -1);
    if (prev) applyRange(prev, selectedRange.end);
  }, [applyRange, neighborVerse, selectedRange.end, selectedRange.start]);

  const expandEnd = useCallback(() => {
    if (!selectedRange.end) return;
    const next = neighborVerse(selectedRange.end, 1);
    if (next) applyRange(selectedRange.start, next);
  }, [applyRange, neighborVerse, selectedRange.end, selectedRange.start]);

  const selectedCount = useMemo(() => {
    if (!selectedRange.start || !selectedRange.end) return 0;
    return collectVerses(selectedRange.start, selectedRange.end, (verse) => verse)
      .length;
  }, [collectVerses, selectedRange.end, selectedRange.start]);

  const previewByFormat = useMemo(() => {
    if (!selectedRange.start || !selectedRange.end) {
      return { reference: "", plain: "", quote: "" };
    }
    return {
      reference: buildFormatText("reference", selectedRange.start, selectedRange.end),
      plain: buildFormatText("plain", selectedRange.start, selectedRange.end),
      quote: buildFormatText("quote", selectedRange.start, selectedRange.end),
    };
  }, [buildFormatText, selectedRange.end, selectedRange.start]);

  const handleCopySelection = useCallback(
    async (format) => {
      const payload = buildFormatText(format, selectedRange.start, selectedRange.end);
      await copyToClipboard(payload);
      if (navigator.vibrate) {
        navigator.vibrate(12);
      }
    },
    [buildFormatText, selectedRange.end, selectedRange.start]
  );

  const handleShareSelection = useCallback(
    (format) => {
      const payload = buildFormatText(format, selectedRange.start, selectedRange.end);
      if (!payload) return;
      const url = `https://wa.me/?text=${encodeURIComponent(payload)}`;
      window.open(url, "_blank", "noopener,noreferrer");
    },
    [buildFormatText, selectedRange.end, selectedRange.start]
  );

  useEffect(() => {
    if (!selectedRange.start || noteEditorOpen) return undefined;
    const timer = window.setTimeout(() => {
      const node = versesRef.current?.querySelector(
        `[data-verse="${selectedRange.start}"]`
      );
      revealElementAboveSelectionDock(node, { isMobile });
    }, 50);
    return () => window.clearTimeout(timer);
  }, [isMobile, noteEditorOpen, selectedRange.start, selectedRange.end, versesRef]);

  useEffect(() => {
    const handleMouseUp = () => {
      window.setTimeout(commitNativeSelection, 0);
    };
    document.addEventListener("mouseup", handleMouseUp);
    return () => {
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [commitNativeSelection]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        clearSelection();
        return;
      }
      const hasRange = selectedRangeRef.current.start;
      if (
        hasRange &&
        (event.metaKey || event.ctrlKey) &&
        event.key.toLowerCase() === "c" &&
        !window.getSelection()?.toString()
      ) {
        event.preventDefault();
        const { start, end } = selectedRangeRef.current;
        copyToClipboard(buildFormatText("reference", start, end));
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [buildFormatText, clearSelection]);

  return {
    selectedRange,
    selectedRangeRef,
    selectedCount,
    previewByFormat,
    buildChapterReference,
    buildFormatText,
    isVerseSelected,
    handleVerseClick,
    expandStart,
    expandEnd,
    clearSelection,
    handleCopySelection,
    handleShareSelection,
    handleCopyTextOnly,
    handleCopyEnhanced,
    handleShareWhatsApp,
  };
};

export default useChapterSelection;
